<template>
  <div>
    <!-- Estado inicial mientras se confirma la orden con el backend -->
    <div v-if="loadingOrder">
      <layout-default :hide-footer-mobile="true">
        <div class="purchase-confirmation">
          <div class="container">
            <div class="loading-order">
              <div class="loading-spinner" aria-hidden="true"></div>
              <div>
                <h6>Estamos confirmando tu compra</h6>
                <p>Esto puede tomar unos segundos. Por favor, no cierres esta ventana.</p>
              </div>
            </div>
          </div>
        </div>
      </layout-default>
    </div>

    <!-- Template para cuando la orden existe y está finalizada -->
    <div v-else-if="order && order.status === 'finished'">
      <layout-default :hide-footer-mobile="true">
        <div class="purchase-confirmation">
          <div class="container">
            
            <!-- Success Banner -->
            <div class="success-banner">
              <img src="/images/featured/tick-circle-bold.svg" alt="success" />
              <div class="banner-content">
                <h6>Tu compra se realizó exitosamente</h6>
                <p>El comprobante de tu compra y la información de pago se enviaron a tu correo <span>{{ financingPartyEmail }}</span></p>
              </div>
            </div>

            <!-- Main Content - Two Columns -->
            <div class="main-content">
              
              <!-- Left Column - Payment Information -->
              <div class="column payment-info">
                <h2>Información de pago</h2>
                <div class="info-card">
                  <div class="info-item">
                    <span>Nº Orden de pedido</span>
                    <p>{{ order?.buyOrder || 'N/A' }}</p>
                  </div>
                  <div class="info-item">
                    <span>Nombre del comercio</span>
                    <p>Preuniversitario Pedro de Valdivia SA</p>
                  </div>
                  <div class="info-item">
                    <span>Monto y moneda</span>
                    <p>${{ formatCurrency(getActualPaidAmount()) }}</p>
                  </div>
                  <div class="info-item">
                    <span>Código de cupón</span>
                    <p>{{ getCouponCode() }}</p>
                  </div>
                  <div class="info-item">
                    <span>Descuento aplicado</span>
                    <p>{{ getCouponDiscountPercentage() }}%</p>
                  </div>
                  <div class="info-item">
                    <span>Fecha de transacción</span>
                    <p>{{ formatDate(order?.createdAt) }}</p>
                  </div>
                  <div class="info-item">
                    <span>Descripción de los bienes y/o servicios</span>
                    <p>Programas PAES</p>
                  </div>
                </div>
              </div>

              <!-- Right Column - Purchase Information -->
              <div class="column purchase-info">
                <h2>Información de tu compra</h2>
                <div class="info-card">
                  <div class="info-item">
                    <span>Programa</span>
                    <p>{{ getProgramName() }}</p>
                  </div>
                  <div class="info-item">
                    <span>Nombre del alumno</span>
                    <p>{{ getStudentName() }}</p>
                  </div>
                  <div class="info-item" v-if="getStudentLocation()">
                    <span>Sede</span>
                    <p>{{ getStudentLocation() }}</p>
                  </div>
                  
                  <!-- Courses Section -->
                  <div class="courses-section">
                    <span class="section-title">Cursos</span>
                    <div 
                      v-for="(course, index) in getStudentCourses()" 
                      :key="index"
                      class="course-item"
                    >
                      <div class="course-header">
                        <div class="course-icon" :style="{ backgroundColor: getCourseColor(course.originalName, course.courseId) }"></div>
                        <span>{{ course.name }}</span>
                      </div>
                      <p class="course-schedule">{{ course.schedule }}</p>
                    </div>
                  </div>

                  <!-- Cost Summary -->
                  <div class="cost-summary">
                  <div class="cost-item" v-if="getTotalCourses() > 0" >
                    <span>Total cursos</span>
                    <span>${{ formatCurrency(getTotalCourses()) }}</span>
                  </div>
                  <div class="cost-item discount" v-if="getDiscountAmount() > 0">
                    <span>Desc. Inscripción online ({{ getDiscountPercentage() }}%)</span>
                    <span>-{{ formatCurrency(getDiscountAmount()) }}</span>
                  </div>
                  <div class="cost-item discount" v-if="getCouponDiscountAmount() > 0">
                    <span>Desc. Cupón {{ getCouponCode() }} ({{ getCouponDiscountPercentage() }}%)</span>
                    <span>-{{ formatCurrency(getCouponDiscountAmount()) }}</span>
                  </div>
                  <div class="cost-item" v-if="getEnrollmentFee() > 0">
                    <span>Matrícula online</span>
                    <span>${{ formatCurrency(getEnrollmentFee()) }}</span>
                  </div>
                  <div class="cost-item total" v-if="total > 0">
                    <span>Monto programa</span>
                    <span>${{ formatCurrency(total) }}</span>
                  </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Top Separator -->
            <div class="top-separator"></div>

            <!-- Total Paid Section -->
            <div class="total-paid" v-if="total > 0">
              <span>Total pagado</span>
              <span class="amount">${{ formatCurrency(total) }}</span>
            </div>

            <!-- Separator -->
            <div class="separator"></div>

            <!-- Action Links -->
            <div class="action-links">
              <div class="action-link">
                <PurchasePdfGenerator 
                  :order="order" 
                  :students="students" 
                  :financingParty="financingParty"
                />
              </div>
              <a href="#" @click.prevent="downloadRegulations" class="action-link">
                <img src="/images/featured/document-download-morado.svg" alt="download" />
                Descargar Reglamento PDV
              </a>
              <a href="#" @click.prevent="resendEmail" class="action-link">
                <img src="/images/featured/sms-morado.svg" alt="email" />
                Enviar por Email
              </a>
            </div>

            <!-- Main Action Button -->
            <div class="main-action">
              <button @click="goToNextStep" class="btn-primary">
                Ir a tu PDV
                <img src="/images/featured/arrow-right.svg" alt="arrow" />
              </button>
            </div>

          </div>
        </div>
      </layout-default>
    </div>

    <!-- Template para cuando la orden no existe -->
    <div v-else>
      <layout-default :hide-footer-mobile="true">
        <div class="purchase-confirmation">
          <div class="container">
            <div class="error-banner">
              <img src="/images/featured/close-circle.svg" alt="error" />
              <div class="banner-content">
                <h6>Orden no encontrada</h6>
                <p>La orden solicitada no existe o ha expirado. Por favor, verifica el número de orden e intenta nuevamente.</p>
              </div>
            </div>
            
            <div class="main-action">
              <button @click="goToLanding" class="btn-primary">
                Ir al inicio
              </button>
            </div>
          </div>
        </div>
      </layout-default>
    </div>
  </div>
</template>

<script>
import LayoutDefault from "@/layouts/Default";
import { orderBy } from "@/helpers";
import formValidation from "@/mixins/formValidation";
import PurchasePdfGenerator from './PurchasePdfGenerator.vue';
import { formatFullCourseName } from '@/utils/subjectNameUtils';
import courseColorsStore from '@/store/courseColorsStore';
import { getLocationDisplayName } from '@/utils/locationName';

export default {
  components: {
    LayoutDefault,
    PurchasePdfGenerator
  },
  data() {
    return {
      total: null,
      order: null,
      loadingOrder: true,
      orderError: null,
      resendLoading: false,
      discount: null,
      serverPath: process.env.VUE_APP_API_URL
    };
  },

  computed: {
    formFinancingParty() {
      return {
        ...this.$store.state.financingParty
      };
    },
    financingPartyName() {
      return `${this.$store.state.financingParty.name} ${this.$store.state.financingParty.lastName}`;
    },
    financingPartyRut() {
      return this.$store.state.financingParty.rut;
    },
    financingPartyEmail() {
      return this.$store.state.financingParty.email;
    },
    financingPartyRegion() {
      if (!this.$store.state.financingParty.Region) return undefined;
      return this.$store.state.financingParty.Commune.Region.name;
    },
    financingPartyCity() {
      if (!this.$store.state.financingParty.City) return undefined;
      return this.$store.state.financingParty.City.name;
    },
    financingPartyCommune() {
      if (!this.$store.state.financingParty.Commune) return undefined;
      return this.$store.state.financingParty.Commune.name;
    },
    students() {
      return this.$store.state.students;
    },
    financingParty() {
      return this.$store.state.financingParty;
    },
  },

  mixins: [formValidation],

  methods: {
    wait(milliseconds) {
      return new Promise(resolve => setTimeout(resolve, milliseconds));
    },
    async fetchOrder(maxAttempts = 4) {
      let lastOrder = null;

      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        const separator = window.location.search ? "&" : "?";
        const response = await this.Axios.get(
          `/orders${window.location.search}${separator}_=${Date.now()}`,
          {
            headers: {
              "Cache-Control": "no-cache",
              Pragma: "no-cache"
            }
          }
        );

        const responseOrder = response.data;
        const hasOrder = responseOrder &&
          (!Array.isArray(responseOrder) || responseOrder.length > 0);

        if (hasOrder) {
          lastOrder = responseOrder;
          if (responseOrder.status !== "payment-confirmed") {
            return responseOrder;
          }
        }

        if (attempt < maxAttempts) {
          await this.wait(750);
        }
      }

      return lastOrder;
    },
    async loadOrder() {
      this.loadingOrder = true;
      this.orderError = null;

      try {
        const order = await this.fetchOrder();
        this.order = order;

        if (!order || order.status !== "finished") {
          this.orderError = new Error("La orden aún no está disponible o no fue finalizada.");
          return;
        }

        await this.$store.dispatch("setOrderInfo", order);
        await this.$store.dispatch("addFinancingPartyInfo", order.FinancingParty);

        if (order.OrderDetails && order.OrderDetails.length > 0) {
          await this.$store.dispatch(
            "addCaretakerInfo",
            order.OrderDetails[0].Student.Caretaker
          );
        }

        await this.$store.dispatch("setStudents", order.sessionState.students);
        this.setTotal();
      } catch (error) {
        console.error("Error al obtener la orden:", error);
        this.order = null;
        this.orderError = error;
      } finally {
        this.loadingOrder = false;
      }
    },
    goToNextStep() {
      this.$router.push({ name: "SuccessInscription" });
    },
    goToLanding() {
      this.$router.push({ name: "Landing" });
    },

    getProgramName() {
      if(this.students.length > 0){
        return this.students[0].program.name;
      }
      return '';
    },
    getStudentName() {
      if(this.students.length > 0){
        const student = this.students[0];
        const name = student.name || '';
        const fatherLastName = student.fatherLastName || '';
        const motherLastName = student.motherLastName || '';
        
        // Construir nombre completo concatenando nombre y apellidos
        let fullName = name.trim();
        if (fatherLastName) {
          fullName += ' ' + fatherLastName.trim();
        }
        if (motherLastName) {
          fullName += ' ' + motherLastName.trim();
        }
        
        return fullName.trim();
      }
      return '';
    },
    getStudentLocation() {
      // Obtener sede directamente de los datos de la API (order.sessionState.students)
      if(this.order && this.order.sessionState && this.order.sessionState.students && this.order.sessionState.students.length > 0) {
        const student = this.order.sessionState.students[0];
        if(student.selectedLocation) {
          return getLocationDisplayName(student.selectedLocation);
        } else if(student.preferredVenue) {
          return getLocationDisplayName(student.preferredVenue);
        }
      }
      return 'Sede no especificada';
    },
    getStudentCourses() {
      if(this.order?.OrderDetails && this.order.OrderDetails.length > 0){
        const courses = this.order.OrderDetails.map(orderDetail => {
          // Usar directamente los datos del curso del backend desde OrderDetails
          const course = orderDetail.Course;
          const originalSubjectName = course?.name || '';
          
          // BUSCAR HORARIO EN sessionState.students
          let schedule = 'Horario no disponible';
          if(this.students && this.students.length > 0) {
            const student = this.students[0];
            if(student.selectedCourses) {
              const studentCourse = student.selectedCourses.find(sc => {
                return String(sc.courseId) === String(course?.id) || String(sc.id) === String(course?.id);
              });
              
              
              if(studentCourse) {
                // Obtener horario del curso del estudiante
                if(studentCourse.hour) {
                  schedule = this.getSchedule(studentCourse.hoursPerDay);
                } else if(studentCourse.hoursPerDay) {
                  schedule = this.getSchedule(studentCourse.hoursPerDay);
                } else if(studentCourse.schedule) {
                  schedule = studentCourse.schedule;
                }
              }
            }
          }
          
          // Crear objeto curso con etiquetas basado en los datos del backend
          // VALIDAR SI ES ELECTIVO SOLO POR EL NOMBRE QUE CONTENGA "ELECTIVO"
          const isElectiveByName = originalSubjectName.toLowerCase().includes('electivo');
          
          const courseWithLabels = {
            ...course,
            hasMention: course?.hasMention || false,
            isElective: isElectiveByName, // SOLO usar la validación por nombre
            isCommon: course?.isCommon || false
          };
          
          // Usar utilidad compartida para formatear nombre completo
          const formattedName = formatFullCourseName(originalSubjectName, courseWithLabels);

          return {
            name: formattedName,
            schedule: schedule,
            courseId: course?.id || orderDetail.courseId,
            originalName: originalSubjectName
          };
        });
        
        // NO agrupar cursos - mostrar cada curso por separado
        return courses;
      }
      return [];
    },
    getCourseColor(courseName, courseId) {
      // Usar el mismo sistema de colores que Step 3 y 4
      return courseColorsStore.getCourseColor(courseId, courseName);
    },
    getTotalCourses() {
      if(this.students.length > 0 && this.students[0].prices){
        return this.students[0].prices.precioSubTotal || 0;
      }
      return 0;
    },
    getDiscountPercentage() {
      // Calcular el porcentaje de descuento general (sin cupón)
      if(this.students.length > 0 && this.students[0].prices){
        const prices = this.students[0].prices;
        const precioSubTotal = prices.precioSubTotal || 0;
        const valorDescuento = prices.valorDescuento || 0;
        
        if (precioSubTotal > 0) {
          return Math.round((valorDescuento / precioSubTotal) * 100);
        }
      }
      return 0;
    },
    getDiscountAmount() {
      if(this.students.length > 0 && this.students[0].prices){
        const prices = this.students[0].prices;
        // Solo el descuento general (sin cupón)
        return prices.valorDescuento || 0;
      }
      return 0;
    },
    getCouponDiscountAmount() {
      if(this.students.length > 0 && this.students[0].prices){
        const prices = this.students[0].prices;
        // Solo el descuento del cupón
        return prices.valorDescuentoCupon || 0;
      }
      return 0;
    },
    getCouponDiscountPercentage() {
      // Detectar específicamente cupones del 100% - PRIORIDAD MÁXIMA
      const cupon = this.getCouponCode();
      if (cupon && cupon.toUpperCase().includes('100')) {
        return 100;
      }
      
      if(this.students.length > 0 && this.students[0].prices){
        const prices = this.students[0].prices;
        const precioSubTotal = prices.precioSubTotal || 0;
        const valorDescuento = prices.valorDescuento || 0;
        const valorDescuentoCupon = prices.valorDescuentoCupon || 0;
        const precioTotal = prices.precioTotal || 0;
        
        // Si el precio total es 0, es un cupón del 100%
        if (precioTotal === 0) {
          return 100;
        }
        
        // Calcular el subtotal después del primer descuento (matrícula online)
        const subtotalDespuesPrimerDescuento = precioSubTotal - valorDescuento;
        
        // Si el descuento del cupón es igual al subtotal restante, es 100%
        if (subtotalDespuesPrimerDescuento > 0 && valorDescuentoCupon >= subtotalDespuesPrimerDescuento) {
          return 100;
        }
        
        // El porcentaje del cupón debe calcularse sobre el subtotal restante
        if (subtotalDespuesPrimerDescuento > 0 && valorDescuentoCupon > 0) {
          return Math.round((valorDescuentoCupon / subtotalDespuesPrimerDescuento) * 100);
        }
      }
      return 0;
    },
    getTotalDiscountPercentage() {
      if(this.students.length > 0 && this.students[0].prices){
        // Sumar los porcentajes individuales de descuento
        const descuentoOnline = this.getDiscountPercentage();
        const descuentoCupon = this.getCouponDiscountPercentage();
        
        return descuentoOnline + descuentoCupon;
      }
      return 0;
    },
    getEnrollmentFee() {
      if(this.students.length > 0 && this.students[0].prices){
        return this.students[0].prices.valorMatricula || 0;
      }
      return 0;
    },
    getActualPaidAmount() {
      // Obtener el monto real pagado desde los datos del estudiante
      if(this.students.length > 0 && this.students[0].prices){
        return this.students[0].prices.precioTotal || 0;
      }
      // Fallback al total del order si no hay datos del estudiante
      return this.order?.total || 0;
    },
    getCouponCode() {
      // Obtener el código del cupón desde los datos del estudiante
      if(this.students.length > 0 && this.students[0].prices){
        const cupon = this.students[0].prices.cupon;
        const validCupon = this.students[0].prices.validCupon;
        
        // Si hay cupón pero no es válido, mostrar "Sin cupón"
        if (cupon && !validCupon) {
          return 'Sin cupón';
        }
        
        // Si hay cupón y es válido, mostrarlo
        if (cupon && validCupon) {
          return cupon;
        }
        
        return 'Sin cupón';
      }
      // Fallback al cupón del order si no hay datos del estudiante
      return this.order?.coupon || 'Sin cupón';
    },
    formatDate(dateString) {
      if(!dateString) return '';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const seconds = date.getSeconds().toString().padStart(2, '0');
        
        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
      } catch (error) {
        console.error('Error formateando fecha:', error);
        return '';
      }
    },
    formatCurrency(amount) {
      if(!amount) return '0';
      return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },
    setTotal() {
        this.total = this.order?.total || 0;
    },
    getSchedule(hoursPerDay){
      if(!hoursPerDay || hoursPerDay.length === 0) return '';
      
      let days = "";
      if (hoursPerDay.length == 1) {
        days = hoursPerDay[0].day + " " + hoursPerDay[0].startTime.slice(0, -3) + " a " + hoursPerDay[0].endTime.slice(0, -3);
      } else if (hoursPerDay.length == 2) {
        if (hoursPerDay[0].startTime.slice(0, -3) == hoursPerDay[1].startTime.slice(0, -3) && hoursPerDay[0].endTime.slice(0, -3) == hoursPerDay[1].endTime.slice(0, -3)) {
          days = hoursPerDay[0].day + " y " + hoursPerDay[1].day + " " + hoursPerDay[0].startTime.slice(0, -3) + " a " + hoursPerDay[0].endTime.slice(0, -3);
        } else {
          days = hoursPerDay[0].day + " " + hoursPerDay[0].startTime.slice(0, -3) + " a " + hoursPerDay[0].endTime.slice(0, -3) + " y " + hoursPerDay[1].day + " " + hoursPerDay[1].startTime.slice(0, -3) + " a " + hoursPerDay[1].endTime.slice(0, -3);
        }
      }
      return days;
    },

    downloadRegulations() {
      if(process.env.VUE_APP_PDV_RULEBOOK) {
        window.open(process.env.VUE_APP_PDV_RULEBOOK, "_blank");
      }
    },
    async resendEmail() {
      this.resendLoading = true;
      try {
        const { data } = await this.Axios.post("/resend", {
          order: this.order?.buyOrder || ''
        });
        if (data.success) {
          alert(`Se ha reenviado el email a ${this.financingPartyEmail}`);
        } else {
          alert(`Ha ocurrido un problema al intentar reenviar el email`);
        }
      } catch (error) {
        alert(`Ha ocurrido un problema al intentar reenviar el email`);
      }
      setTimeout(() => {
        this.resendLoading = false;
      }, 1000);
    }
  },

  async created() {
    // La orden se consulta primero para no bloquear la confirmación con datos auxiliares.
    this.loadOrder();

    this.Axios.get("/dashadmin/infogeneral")
      .then(response => {
        this.discount = response.data[0].discount;
      })
      .catch(error => {
        console.error("Error al obtener la información general:", error);
      });

    this.Axios.get("/regions")
      .then(response => {
        this.availableRegions = orderBy([...response.data], "name");
      })
      .catch(error => {
        console.error("Error al obtener las regiones:", error);
      });
  },

  destroyed() {
    localStorage.clear();
  },
  beforeDestroy() {
    localStorage.clear();
  },
};
</script>

<style scoped lang="scss">
.purchase-confirmation {
  padding: 40px 0;
  background: #fff;
  min-height: 100vh;

  .container {
    max-width: 936px;
    margin: 0 auto;
    padding: 0 24px;

    @media (min-width: 769px) {
      padding: 0 76px;
    }
  }

  .loading-order {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 20px 24px;
    background: #f7f5ff;
    border: 1px solid #d8cff5;
    border-radius: 8px;

    h6 {
      margin: 0 0 4px;
      color: #2f235a;
      font-size: 18px;
      font-weight: 700;
    }

    p {
      margin: 0;
      color: #5f5875;
      font-size: 14px;
    }
  }

  .loading-spinner {
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    border: 3px solid #d8cff5;
    border-top-color: #5b3f99;
    border-radius: 50%;
    animation: loading-order-spin 0.8s linear infinite;
  }

  // Success Banner
  .success-banner {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 16px;
    background: #F0FFF7;
    border: 1px solid #4EEE9E;
    border-radius: 8px;
    margin-bottom: 40px;

    img {
      width: 24px;
      height: 24px;
      flex-shrink: 0;
    }

          .banner-content {
        h6 {
          font-family: 'Poppins-Medium', sans-serif;
          color: #0F8047;
          font-size: 18px;
          font-weight: 500;
          line-height: 26px;
          margin: 0 0 4px 0;
        }

        p {
          color: #0F8047;
          font-family: 'Poppins-Regular', sans-serif;
          font-size: 16px;
          font-weight: 400;
          line-height: 24px;
          margin: 0;

          span {
            font-family: 'Poppins-Bold', sans-serif;
            font-weight: 700;
            color: #0F8047;
          }
        }
      }
  }

  // Main Content - Two Columns
  .main-content {
    display: flex;
    gap: 40px;
    margin-bottom: 40px;

    @media (max-width: 768px) {
      flex-direction: column;
    }

    .column {
      flex: 1;

      h2 {
        color: #000;
        font-family: 'Poppins-Bold', sans-serif;
        font-size: 36px;
        font-weight: 700;
        line-height: 46px;
        margin: 0 0 24px 0;

        @media (max-width: 768px) {
          font-size: 20px;
          line-height: 28px;
        }
      }

      .info-card {
        background: #fff;
        padding: 16px;

        .info-item {
          display: flex;
          flex-direction: column;
          margin-bottom: 12px;

          &:last-of-type {
            margin-bottom: 0;
          }

          span {
            color: #666;
            font-family: 'Poppins-Regular', sans-serif;
            font-size: 16px;
            font-weight: 400;
            line-height: 24px;
            margin-bottom: 4px;
          }

          p {
            color: #000;
            font-family: 'Poppins-Medium', sans-serif;
            font-size: 18px;
            font-weight: 500;
            line-height: 26px;
            margin: 0;
          }
        }

        // Courses Section
        .courses-section {
          margin: 24px 0;

          .section-title {
            color: #666;
            font-family: 'Poppins-Bold', sans-serif;
            font-size: 16px;
            font-weight: 700;
            line-height: 24px;
            display: block;
            margin-bottom: 12px;
          }

          .course-item {
            margin-bottom: 16px;

            &:last-child {
              margin-bottom: 0;
            }

            .course-header {
              display: flex;
              align-items: center;
              gap: 8px;
              margin-bottom: 4px;

              .course-icon {
                width: 12px;
                height: 12px;
                border-radius: 50%;

                &.teal {
                  background: #20B2AA;
                }

                &.orange {
                  background: #FFA500;
                }
              }

              span {
                color: #000;
                font-family: 'Poppins-Medium', sans-serif;
                font-size: 18px;
                font-weight: 500;
                line-height: 26px;
              }
            }

            .course-schedule {
              color: #666;
              font-family: 'Poppins-Regular', sans-serif;
              font-size: 16px;
              font-weight: 400;
              line-height: 24px;
              margin: 0;
              padding-left: 20px;
            }
          }
        }

        // Cost Summary
        .cost-summary {
          border-top: 1px dashed #CACBCE;
          padding-top: 24px;

          .cost-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 12px;

            &:last-child {
              margin-bottom: 0;
            }

            span {
              font-family: 'Poppins-Regular', sans-serif;
              font-size: 16px;
              font-weight: 400;
              line-height: 24px;
            }

            &:first-child {
              span {
                color: #000;
              }
            }

            &.discount {
              span {
                color: #0F8047;
                font-size: 14px;
                line-height: 18px;
              }
            }

            &.total {
              span {
                color: #000;
                font-size: 18px;
                line-height: 26px;

                &:last-child {
                  color: #000;
                  font-family: 'Poppins-Bold', sans-serif;
                  font-size: 24px;
                  font-weight: 700;
                  line-height: 32px;
                }
              }
            }
          }
        }
      }
    }
  }

  // Total Paid Section
  .total-paid {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;

    span {
      font-family: 'Poppins', sans-serif;
      font-size: 24px;
      font-weight: 400;
      line-height: 32px;
      color: #666;
      letter-spacing: 0px;

      &.amount {
        font-family: 'Poppins', sans-serif;
        font-size: 24px;
        font-weight: 700;
        line-height: 32px;
        color: #000;
        letter-spacing: 0px;
      }
    }
  }

  // Top Separator
  .top-separator {
    width: 100%;
    height: 1px;
    background: #ccc;
    margin: 40px 0 24px 0;
  }

  // Separator
  .separator {
    width: 100%;
    height: 1px;
    background: repeating-linear-gradient(
      to right,
      #ccc 0,
      #ccc 4px,
      transparent 4px,
      transparent 8px
    );
    margin-bottom: 32px;
  }

  // Action Links
  .action-links {
    display: flex;
    justify-content: center;
    gap: 48px;
    margin-bottom: 32px;

    @media (max-width: 768px) {
      flex-direction: column;
      align-items: center;
      gap: 16px;
    }

    .action-link {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #3691D8;
      font-family: 'Poppins-Medium', sans-serif;
      font-size: 16px;
      font-weight: 500;
      line-height: 24px;
      text-decoration: none;
      transition: opacity 0.2s;
      white-space: nowrap;

      &:hover {
        opacity: 0.8;
      }

      img {
        width: 18px;
        height: 18px;
      }
    }
  }

  // Error Banner (para orden no encontrada)
  .error-banner {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 16px;
    background: #FFF5F5;
    border: 1px solid #F56565;
    border-radius: 8px;
    margin-bottom: 40px;

    img {
      width: 24px;
      height: 24px;
      flex-shrink: 0;
    }

    .banner-content {
      h6 {
        font-family: 'Poppins-Medium', sans-serif;
        color: #C53030;
        font-size: 18px;
        font-weight: 500;
        line-height: 26px;
        margin: 0 0 4px 0;
      }

      p {
        color: #C53030;
        font-family: 'Poppins-Regular', sans-serif;
        font-size: 16px;
        font-weight: 400;
        line-height: 24px;
        margin: 0;
      }
    }
  }

  // Main Action Button
  .main-action {
    display: flex;
    justify-content: center;

    .btn-primary {
      display: flex;
      align-items: center;
      gap: 8px;
      background: #000;
      color: #fff;
      border: none;
      border-radius: 50px;
      padding: 16px 32px;
      font-family: 'Poppins-Medium', sans-serif;
      font-size: 18px;
      font-weight: 500;
      line-height: 26px;
      cursor: pointer;
      transition: background-color 0.2s;

      &:hover {
        background: #333;
      }

      img {
        width: 18px;
        height: 18px;
      }
    }
  }
}

@keyframes loading-order-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
